import { useEffect, useState } from 'react'

import cn from 'classnames'
import { toast } from 'react-hot-toast'
import Skeleton from 'react-loading-skeleton'
import { MetaMaskAvatar } from 'react-metamask-avatar'
import { useLocation } from 'react-router-dom'

import ChainToken from '@src/components/ChainToken'
import CopyToClipboard from '@src/components/CopyToClipboard'
import LogoutIcon from '@src/components/icons/Logout'
import NoWalletIcon from '@src/components/icons/NoWallet'
import { ChainId, getChainNameFromChainId } from '@src/configs/chainInfo'
import * as PATH from '@src/configs/paths'
import { MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { useFetchAllBalances } from '@src/hooks/useFetchAllBalances'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { IToken } from '@src/types/token'
import { Balance } from '@src/utils/balance'
import { bigintPow } from '@src/utils/bigintPow'
import { formatNumber } from '@src/utils/formatFee'
import { createContextLogger } from '@src/utils/logger'
import { truncateAddress } from '@src/utils/truncateAddress'
import { useApplicationStore, useTokensStore } from '@src/zustand_stores'

interface Props {
    open: boolean
    onClose: () => void
    onLogout: () => void
    onTokenSelect?: (token: IToken) => void
}

interface GroupedToken {
    symbol: string
    name: string
    tokens: IToken[]
    totalBalanceUSD: number
    logoURI: string
}

const logger = createContextLogger('WalletDrawer')

const WalletPortfolioDrawer: React.FC<Props> = ({ open, onClose, onLogout }: Props) => {
    const [isLoading, setIsLoading] = useState(true)

    /**
     * * * * * * * * * * * * * * * * * * * * *  NEW ZUSTAND STORES * * * * * * * * * * * * * * *
     */
    const { isEVMConnected, EVMChainId, evmWalletAddress } = useWalletConnections()
    const generatedReferralCode = useApplicationStore(state => state.generatedReferralCode)

    const { tokens: zustandTokens, isLoading: zustandTokensLoading } = useTokensStore()
    const { isLoading: balanceResponsesLoading } = useFetchAllBalances()

    useEffect(() => {
        setIsLoading(
            zustandTokensLoading ||
                balanceResponsesLoading ||
                (!zustandTokensLoading && !balanceResponsesLoading && zustandTokens.length === 0)
        )
    }, [zustandTokensLoading, balanceResponsesLoading, zustandTokens])
    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    const [internalOpen, setInternalOpen] = useState<boolean>(false)
    useEffect(() => {
        setInternalOpen(open)
    }, [open])

    const [totalBalance, setTotalBalance] = useState(0.1)
    const [totalBalanceEVM, setTotalBalanceEVM] = useState(0.1)
    const [totalBalanceTron, setTotalBalanceTron] = useState(0.1)
    const [totalBalanceSolana, setTotalBalanceSolana] = useState(0.1)

    useEffect(() => {
        setTotalBalance(totalBalanceEVM + totalBalanceTron + totalBalanceSolana)
    }, [totalBalanceEVM, totalBalanceTron, totalBalanceSolana])

    useEffect(() => {
        if (!isEVMConnected) {
            // close out the portfolio drawer
            setInternalOpen(false)
            setTimeout(() => onClose(), 300)
        }
    }, [isEVMConnected])

    const preFilter = (token: IToken) => {
        try {
            const tokenBalanceThreshold = bigintPow(
                BigInt(10),
                BigInt(Math.max(0, token.decimals - MAX_TOKEN_DISPLAY_DECIMALS))
            )
            return (
                token.userBalance !== undefined &&
                token.userBalance > tokenBalanceThreshold &&
                token.userBalanceInUSD &&
                token.userBalanceInUSD > 0
            )
        } catch (e) {
            logger.error(`Cannot filter token:`, token)
            return false
        }
    }

    const sortTokens = (tokens: IToken[]): GroupedToken[] => {
        // Group tokens by symbol
        const groupedBySymbol = tokens.reduce<Record<string, GroupedToken>>((acc, token) => {
            if (!token.symbol) return acc

            const key = token.symbol
            if (!acc[key]) {
                acc[key] = {
                    symbol: token.symbol,
                    name: token.name || token.symbol,
                    tokens: [],
                    totalBalanceUSD: 0,
                    logoURI: token.logoURI || '',
                }
            }
            acc[key].tokens.push(token)
            acc[key].totalBalanceUSD += token.userBalanceInUSD || 0
            return acc
        }, {})

        // Convert to array and sort by USD value
        const groupedTokens = Object.values(groupedBySymbol)
        return groupedTokens.sort((a, b) => b.totalBalanceUSD - a.totalBalanceUSD)
    }

    const [filteredEVMTokens, setFilteredEVMTokens] = useState<IToken[]>([])
    const [filteredTronTokens, setFilteredTronTokens] = useState<IToken[]>([])
    const [filteredSolanaTokens, setFilteredSolanaTokens] = useState<IToken[]>([])

    useEffect(() => {
        if (zustandTokens.length > 0) {
            const filteredTokens = zustandTokens.filter(preFilter)

            const _filteredEVMTokens = filteredTokens.filter(
                t => t.chainId !== ChainId.Solana && t.chainId !== ChainId.Tron
            )
            const sumBalanceEVM = _filteredEVMTokens.reduce((p, c) => p + (c.userBalanceInUSD || 0), 0)
            setTotalBalanceEVM(sumBalanceEVM)

            const _filteredTronTokens = filteredTokens.filter(t => t.chainId === ChainId.Tron)
            const sumBalanceTron = _filteredTronTokens.reduce((p, c) => p + (c.userBalanceInUSD || 0), 0)
            setTotalBalanceTron(sumBalanceTron)

            const _filteredSolanaTokens = filteredTokens.filter(t => t.chainId === ChainId.Solana)
            const sumBalanceSolana = _filteredSolanaTokens.reduce((p, c) => p + (c.userBalanceInUSD || 0), 0)
            setTotalBalanceSolana(sumBalanceSolana)

            setFilteredEVMTokens(_filteredEVMTokens)
            setFilteredTronTokens(_filteredTronTokens)
            setFilteredSolanaTokens(_filteredSolanaTokens)
        }
    }, [zustandTokens])

    const WalletBalanceListItem: React.FC<{ groupedToken: GroupedToken }> = ({ groupedToken }) => {
        const setWalletSelectedSellToken = useApplicationStore(state => state.setWalletSelectedSellToken)
        const location = useLocation()
        const [isExpanded, setIsExpanded] = useState(false)

        // Sort tokens within group by USD value
        const sortedTokens = [...groupedToken.tokens].sort(
            (a, b) => (b.userBalanceInUSD || 0) - (a.userBalanceInUSD || 0)
        )

        const totalBalance = sortedTokens.reduce((total, token) => {
            if (!token.userBalance) return total
            const balance = new Balance(token)
                .setValue(token.userBalance)
                .formatWithMagnitudes(MAX_TOKEN_DISPLAY_DECIMALS)

            // Remove commas before parsing
            const cleanBalance = balance.replace(/,/g, '')
            return total + (parseFloat(cleanBalance) || 0)
        }, 0)

        const handleTokenSelect = (token: IToken) => {
            logger.info(`Selected token:`, token)
            if (location.pathname === PATH.SWAP) {
                setWalletSelectedSellToken(token)
            }
            setInternalOpen(false)
            setTimeout(() => onClose(), 300)
        }

        return (
            <>
                <div
                    className='flex w-full cursor-pointer items-center space-x-3 px-4 py-2.5 transition-all duration-300 hover:bg-[#22242A]'
                    onClick={() => {
                        if (sortedTokens.length > 1) {
                            setIsExpanded(!isExpanded)
                        } else if (sortedTokens[0]) {
                            handleTokenSelect(sortedTokens[0])
                        }
                    }}
                >
                    <div className='relative'>
                        <ChainToken tokenLogo={groupedToken.logoURI} />
                        <div className='absolute -bottom-1.5 -right-1.5 flex -space-x-2'>
                            {sortedTokens.slice(0, 3).map(token => (
                                <img
                                    key={token.chainId}
                                    src={`/assets/network/${getChainNameFromChainId(token.chainId)}.png`}
                                    className='h-5 w-5 rounded-full border-2 border-modal-background'
                                    alt={getChainNameFromChainId(token.chainId)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className='flex-1 text-sm'>
                        <p className='font-semibold'>{groupedToken.name}</p>
                        <p className='font-medium text-gray-400'>
                            {totalBalance.toFixed(6)}&nbsp;{groupedToken.symbol}
                        </p>
                    </div>

                    <div className='flex items-center space-x-2'>
                        <p className='text-sm font-bold'>${formatNumber(groupedToken.totalBalanceUSD)}</p>
                        {sortedTokens.length > 1 && (
                            <svg
                                className={cn('h-4 w-4 transition-transform', isExpanded ? 'rotate-180' : '')}
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M6 9L12 15L18 9'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        )}
                    </div>
                </div>

                {/* Dropdown for individual network tokens */}
                <div
                    className={cn(
                        'overflow-hidden transition-all duration-300',
                        isExpanded ? 'max-h-[500px]' : 'max-h-0'
                    )}
                >
                    {sortedTokens.map(token => (
                        <div
                            key={token.chainId}
                            className='flex w-full cursor-pointer items-center space-x-3 bg-[#1D1F23] px-6 py-2 text-[13px] transition-all duration-300 hover:bg-[#22242A]'
                            onClick={() => handleTokenSelect(token)}
                        >
                            <div className='relative'>
                                <ChainToken
                                    tokenLogo={token.logoURI}
                                    networkIcon={`/assets/network/${getChainNameFromChainId(token.chainId)}.png`}
                                    chainIconSize={18}
                                    tokenIconSize={32}
                                />
                            </div>

                            <div className='flex-1'>
                                <p className='font-semibold'>{token.name}</p>
                                <p className='font-medium text-gray-400'>
                                    {token.userBalance !== undefined
                                        ? new Balance(token)
                                              .setValue(token.userBalance)
                                              .formatWithMagnitudes(MAX_TOKEN_DISPLAY_DECIMALS)
                                        : '0'}
                                    &nbsp;{token.symbol}
                                </p>
                            </div>

                            <p className='font-bold'>${formatNumber(token.userBalanceInUSD || 0)}</p>
                        </div>
                    ))}
                </div>
            </>
        )
    }

    const renderAllBalances = () => {
        if (!filteredEVMTokens.length && !filteredTronTokens.length && !filteredSolanaTokens.length) return null

        // Combine all tokens
        const allTokens = [...filteredEVMTokens, ...filteredTronTokens, ...filteredSolanaTokens]

        // Sort all tokens together
        const groupedTokens = sortTokens(allTokens)
        return groupedTokens.map((group, idx) => (
            <WalletBalanceListItem key={`${group.symbol}-${idx}`} groupedToken={group} />
        ))
    }

    const renderLoader = (count: number = 5) => {
        return Array(count)
            .fill(0)
            .map((_, id) => (
                <div key={`skeleton-${id}`} className='flex w-full items-center space-x-3 px-4 py-2'>
                    <Skeleton enableAnimation baseColor='#9B9B9B' circle className='h-9 w-9' />
                    <div>
                        <Skeleton enableAnimation baseColor='#9B9B9B' className='w-20' />
                        <Skeleton enableAnimation baseColor='#9B9B9B' className='w-20' />
                    </div>
                    <Skeleton enableAnimation baseColor='#9B9B9B' className='w-20' containerClassName='!ml-auto' />
                </div>
            ))
    }

    return (
        <div
            className={cn(
                'fixed bottom-0 right-0 z-[100] h-[calc(100%-60px)] overflow-hidden md:h-full',
                open ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
        >
            <div
                className='fixed bottom-0 left-0 right-0 top-0 z-10 cursor-pointer bg-black/50 backdrop-blur-sm'
                onClick={() => {
                    setInternalOpen(false)
                    setTimeout(() => onClose(), 300)
                }}
            ></div>
            <div
                className={cn(
                    'relative z-20 h-full w-[420px] max-w-[420px] space-y-3 overflow-auto bg-[#141618] transition-all duration-300',
                    internalOpen ? 'translate-0' : 'translate-y-full md:translate-x-full md:translate-y-0'
                )}
                style={{
                    boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                }}
            >
                <div className='space-y-4 pt-5'>
                    <h2 className='px-4 text-2xl font-semibold'>Portfolio</h2>

                    <div className='flex items-center justify-between px-4'>
                        <div
                            className='group flex cursor-pointer items-center gap-3'
                            onClick={() => {
                                if (evmWalletAddress) {
                                    navigator.clipboard.writeText(evmWalletAddress)
                                    toast.success('Address copied!', {
                                        duration: 2000,
                                        position: 'top-right',
                                        style: {
                                            background: '#16a34a',
                                            color: '#fff',
                                            fontWeight: 500,
                                            fontSize: '14px',
                                            padding: '12px 16px',
                                            borderRadius: '6px',
                                        },
                                    })
                                }
                            }}
                        >
                            <div className='relative'>
                                <MetaMaskAvatar address={evmWalletAddress || ''} size={24} />
                                {EVMChainId && (
                                    <img
                                        src={`/assets/network/${getChainNameFromChainId(EVMChainId)}.png`}
                                        className='absolute -bottom-1 -right-1 h-4 w-4 rounded-full border border-[#141618]'
                                        alt={getChainNameFromChainId(EVMChainId)}
                                    />
                                )}
                            </div>
                            <p className='rounded-md px-3 py-1.5 text-[16px] font-semibold text-gray-200 transition-all duration-300 group-hover:bg-[#22242A] group-hover:text-white'>
                                {truncateAddress(evmWalletAddress || '', { evmPrefixLen: 5, evmSuffixLen: 5 })}
                            </p>
                        </div>
                        <button
                            onClick={onLogout}
                            className='flex items-center gap-2 rounded-md bg-[#22242A] px-3 py-1.5 text-sm font-medium text-gray-300 transition-colors hover:bg-[#2A2D33]'
                        >
                            <LogoutIcon className='h-4 w-4' />
                            Disconnect
                        </button>
                    </div>

                    {evmWalletAddress &&
                        generatedReferralCode.code !== '' &&
                        generatedReferralCode.userAddress.toLowerCase() === evmWalletAddress.toLowerCase() && (
                            <div className='flex items-center px-4'>
                                <p className='text-[13px] text-gray-400'>
                                    Referral Code:{' '}
                                    <span className='ml-1 font-medium text-[#85C3F8]'>
                                        {generatedReferralCode.code}
                                    </span>
                                </p>
                                <div className='ml-2'>
                                    <CopyToClipboard
                                        textToCopy={generatedReferralCode.code}
                                        textType='Referral Code'
                                        anchorId='referral-code'
                                    />
                                </div>
                            </div>
                        )}

                    <h3 className='px-4 text-5xl font-bold'>
                        {isLoading ? (
                            <Skeleton enableAnimation baseColor='#9B9B9B' className='h-12 w-24' />
                        ) : (
                            <>${Number(formatNumber(totalBalance)).toFixed(2)}</>
                        )}
                    </h3>
                </div>

                <div className='!mt-4'>
                    {isLoading ? (
                        renderLoader()
                    ) : (
                        <>
                            <p className='px-4 pb-2'>
                                All Networks
                                <span className='text-sm text-gray-400'>
                                    &nbsp;(${Number(formatNumber(totalBalance)).toFixed(2)})
                                </span>
                            </p>
                            {renderAllBalances()}
                        </>
                    )}
                </div>
                {isLoading == false && totalBalance <= 0 && (
                    <div className='margin-auto'>
                        <NoWalletIcon style={{ margin: 'auto' }} />
                        <div className='space-y-3 text-center'>
                            <p className='text-xl font-semibold'>No balances found</p>
                            <span className='text-xs font-medium'>Sure you&apos;ve got the right wallet?</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default WalletPortfolioDrawer
